import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { Search, Check, X, Loader, Plus, Shield, RotateCcw, ChevronDown, Sparkles, SunMedium, MoonStar } from 'lucide-react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Add-on compatibility rules
const ADDON_COMPATIBILITY = {
  'Enterprise Mobility + Security E3': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Enterprise Mobility + Security E5': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Defender for Office 365 P1': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High'],
    incompatible: ['Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Defender for Office 365 P2': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High'],
    incompatible: ['Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Defender for Endpoint P1': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High'],
    incompatible: ['Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Defender for Endpoint P2': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High'],
    incompatible: ['Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Defender for Identity': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Defender for Cloud Apps': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Teams Phone Standard': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
    incompatible: [],
  },
  'Teams Calling Plan': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'Frontline', 'Frontline GCC'],
    incompatible: ['GCC High', 'Frontline GCC High'],
  },
  'Audio Conferencing': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'Frontline', 'Frontline GCC'],
    incompatible: ['GCC High', 'Frontline GCC High'],
  },
  'Teams Premium': {
    compatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
    incompatible: [],
  },
  'Microsoft Purview Audit (Premium)': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Purview eDiscovery (Premium)': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Purview Insider Risk': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft Purview DLP': {
    compatible: ['Commercial Enterprise', 'GCC', 'GCC High'],
    incompatible: ['Business', 'Frontline', 'Frontline GCC', 'Frontline GCC High'],
  },
  'Microsoft 365 F5 Security': {
    compatible: ['Frontline'],
    incompatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High'],
  },
  'Microsoft 365 F5 Compliance': {
    compatible: ['Frontline'],
    incompatible: ['Commercial Enterprise', 'Business', 'GCC', 'GCC High'],
  },
};

const isAddonCompatible = (addonName, selectedBaseLicenses) => {
  if (selectedBaseLicenses.length === 0) return true;
  const rules = ADDON_COMPATIBILITY[addonName];
  if (!rules) return true;
  return selectedBaseLicenses.some(base => {
    const baseType = base.type;
    if (rules.compatible.length > 0) {
      return rules.compatible.some(compatType => baseType.includes(compatType));
    }
    return !rules.incompatible.some(incompatType => baseType.includes(incompatType));
  });
};

const FeatureCell = ({ value, isFromAddon, upgradeHint }) => {
  if (value === true) {
    return (
      <div className={`flex items-center justify-center p-2 ${isFromAddon ? 'feature-addon-ring' : ''}`}>
        <Check className={`w-5 h-5 feature-glyph ${isFromAddon ? 'text-amber-400' : 'text-emerald-400'}`} />
        {isFromAddon && <Plus className="w-3 h-3 text-amber-400 -ml-1 -mt-2" />}
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex items-center justify-center group relative">
        <X className={`w-5 h-5 feature-glyph text-rose-400/60 ${upgradeHint ? 'cursor-help' : ''}`} />
        {upgradeHint && (
          <div className="tooltip min-w-max">
            <div className="font-semibold text-amber-400 mb-2">Get this feature with:</div>
            {upgradeHint}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={`text-center text-sm theme-copy-muted p-2 ${isFromAddon ? 'feature-addon-ring' : ''}`}>
      {value}
      {isFromAddon && <Plus className="w-3 h-3 text-amber-400 inline ml-1" />}
    </div>
  );
};

// Liquid glass select styles
const liquidGlassSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    background: 'var(--select-control-bg)',
    backdropFilter: 'blur(10px)',
    borderColor: state.isFocused ? 'var(--select-border-focus)' : 'var(--select-border)',
    borderRadius: '14px',
    minHeight: '48px',
    boxShadow: state.isFocused ? '0 0 0 4px var(--select-ring)' : 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: 'var(--select-border-hover)',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--select-text)',
  }),
  multiValue: (provided, state) => ({
    ...provided,
    background: state.data.isAddon ? 'rgba(245, 158, 11, 0.3)' : 'rgba(99, 102, 241, 0.3)',
    borderRadius: '8px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'white',
    fontWeight: 500,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: 'white',
    borderRadius: '0 8px 8px 0',
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.5)',
      color: 'white',
    },
  }),
  menu: (provided) => ({
    ...provided,
    background: 'var(--select-menu-bg)',
    backdropFilter: 'blur(20px)',
    border: '1px solid var(--select-border)',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: 'var(--select-menu-shadow)',
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '8px',
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isFocused ? 'var(--select-option-hover)' : 'transparent',
    color: 'var(--select-text)',
    borderRadius: '10px',
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:active': {
      background: 'var(--select-option-active)',
    },
  }),
  group: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  groupHeading: (provided) => ({
    ...provided,
    background: 'rgba(99, 102, 241, 0.1)',
    color: 'rgba(129, 140, 248, 1)',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '10px 16px',
    marginBottom: '4px',
    borderRadius: '10px',
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--select-text)',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--select-muted)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--select-muted)',
    '&:hover': {
      color: 'var(--select-text)',
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: 'var(--select-muted)',
    '&:hover': {
      color: 'var(--accent-danger)',
    },
  }),
};

const addonSelectStyles = {
  ...liquidGlassSelectStyles,
  control: (provided, state) => ({
    ...liquidGlassSelectStyles.control(provided, state),
    background: 'var(--select-addon-control-bg)',
    borderColor: state.isFocused ? 'var(--select-addon-border-focus)' : 'var(--select-addon-border)',
    boxShadow: state.isFocused ? '0 0 0 4px var(--select-addon-ring)' : 'none',
  }),
  groupHeading: (provided) => ({
    ...provided,
    background: 'var(--select-addon-heading-bg)',
    color: 'var(--select-addon-heading-text)',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '10px 16px',
    marginBottom: '4px',
    borderRadius: '10px',
  }),
};

function App() {
  const [licenses, setLicenses] = useState([]);
  const [sideABase, setSideABase] = useState(null);
  const [sideAAddons, setSideAAddons] = useState([]);
  const [sideBBase, setSideBBase] = useState(null);
  const [sideBAddons, setSideBAddons] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeSuggestions, setUpgradeSuggestions] = useState({});
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('theme') || 'dark';
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [licensesRes, suggestionsRes] = await Promise.all([
        fetch(`${API_URL}/api/licenses/`),
        fetch(`${API_URL}/api/features/upgrade-suggestions`)
      ]);

      if (!licensesRes.ok) {
        throw new Error(`API error: ${licensesRes.status}`);
      }
      const licensesData = await licensesRes.json();
      setLicenses(licensesData);

      if (suggestionsRes.ok) {
        const suggestionsData = await suggestionsRes.json();
        setUpgradeSuggestions(suggestionsData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch initial data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const sideA = useMemo(() => [...(sideABase ? [sideABase] : []), ...sideAAddons], [sideABase, sideAAddons]);
  const sideB = useMemo(() => [...(sideBBase ? [sideBBase] : []), ...sideBAddons], [sideBBase, sideBAddons]);

  const clearAllSelections = () => {
    setSideABase(null);
    setSideAAddons([]);
    setSideBBase(null);
    setSideBAddons([]);
    setComparisonData(null);
    setSearchTerm('');
  };

  const fetchComparison = async () => {
    if (sideA.length === 0 || sideB.length === 0) {
      alert('Please select at least one license for each side.');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/compare/aggregate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          side_a_ids: sideA.map(l => l.value),
          side_b_ids: sideB.map(l => l.value),
        }),
      });
      const data = await res.json();
      setComparisonData(data);
      setExpandedCategories(new Set(Object.keys(data.feature_comparison)));
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const filteredData = useMemo(() => {
    if (!comparisonData) return [];
    if (!searchTerm) return Object.entries(comparisonData.feature_comparison);

    const term = searchTerm.toLowerCase();
    return Object.entries(comparisonData.feature_comparison)
      .map(([category, features]) => ([
        category,
        features.filter(feature =>
          feature.name.toLowerCase().includes(term) ||
          category.toLowerCase().includes(term)
        )
      ]))
      .filter(([_, features]) => features.length > 0);
  }, [searchTerm, comparisonData]);

  const { baseLicenseOptions, allAddonOptions } = useMemo(() => {
    const baseGroups = {};
    const addonGroups = {};

    licenses.forEach(l => {
      const type = l.license_type;
      const option = {
        value: l.id,
        label: l.name,
        isAddon: l.is_addon,
        type: l.license_type,
      };

      if (l.is_addon) {
        if (!addonGroups[type]) addonGroups[type] = [];
        addonGroups[type].push(option);
      } else {
        if (!baseGroups[type]) baseGroups[type] = [];
        baseGroups[type].push(option);
      }
    });

    return {
      baseLicenseOptions: Object.entries(baseGroups).map(([type, options]) => ({
        label: type,
        options: options
      })),
      allAddonOptions: Object.entries(addonGroups).map(([type, options]) => ({
        label: type,
        options: options
      }))
    };
  }, [licenses]);

  const sideAAddonOptions = useMemo(() => {
    if (!sideABase) return allAddonOptions;
    return allAddonOptions.map(group => ({
      label: group.label,
      options: group.options.filter(addon => isAddonCompatible(addon.label, [sideABase]))
    })).filter(group => group.options.length > 0);
  }, [allAddonOptions, sideABase]);

  const sideBAddonOptions = useMemo(() => {
    if (!sideBBase) return allAddonOptions;
    return allAddonOptions.map(group => ({
      label: group.label,
      options: group.options.filter(addon => isAddonCompatible(addon.label, [sideBBase]))
    })).filter(group => group.options.length > 0);
  }, [allAddonOptions, sideBBase]);

  useEffect(() => {
    if (sideABase && sideAAddons.length > 0) {
      const compatibleAddons = sideAAddons.filter(addon =>
        isAddonCompatible(addon.label, [sideABase])
      );
      if (compatibleAddons.length !== sideAAddons.length) {
        setSideAAddons(compatibleAddons);
      }
    }
  }, [sideABase]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sideBBase && sideBAddons.length > 0) {
      const compatibleAddons = sideBAddons.filter(addon =>
        isAddonCompatible(addon.label, [sideBBase])
      );
      if (compatibleAddons.length !== sideBAddons.length) {
        setSideBAddons(compatibleAddons);
      }
    }
  }, [sideBBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const sideALabel = useMemo(() => {
    if (sideA.length === 0) return 'Side A';
    if (sideA.length === 1) return sideA[0].label;
    const baseName = sideABase ? sideABase.label.replace(/Microsoft 365 |Office 365 /g, '') : '';
    const addonNames = sideAAddons.map(l => l.label.replace(/Microsoft |Enterprise Mobility \+ Security /g, ''));
    if (addonNames.length > 0) {
      return `${baseName} + ${addonNames.join(' + ')}`;
    }
    return baseName;
  }, [sideA, sideABase, sideAAddons]);

  const sideBLabel = useMemo(() => {
    if (sideB.length === 0) return 'Side B';
    if (sideB.length === 1) return sideB[0].label;
    const baseName = sideBBase ? sideBBase.label.replace(/Microsoft 365 |Office 365 /g, '') : '';
    const addonNames = sideBAddons.map(l => l.label.replace(/Microsoft |Enterprise Mobility \+ Security /g, ''));
    if (addonNames.length > 0) {
      return `${baseName} + ${addonNames.join(' + ')}`;
    }
    return baseName;
  }, [sideB, sideBBase, sideBAddons]);

  const getUpgradeHint = (featureId, selectedBaseLicense) => {
    const suggestion = upgradeSuggestions[featureId];
    if (!suggestion) return null;

    const selectedBaseLicenses = selectedBaseLicense ? [selectedBaseLicense] : [];
    const selectedTypes = selectedBaseLicenses.map(l => l.type);

    const compatibleAddons = suggestion.addons.filter(addon => {
      return isAddonCompatible(addon.name, selectedBaseLicenses);
    });

    const relevantUpgrades = suggestion.base_licenses.filter(base => {
      const alreadySelected = selectedBaseLicenses.some(sel => sel.value === base.id);
      if (alreadySelected) return false;
      if (selectedTypes.length > 0) {
        const baseType = base.type || '';
        return selectedTypes.some(selType =>
          (selType.includes('GCC') && baseType.includes('GCC')) ||
          (selType.includes('Enterprise') && baseType.includes('Enterprise')) ||
          (selType.includes('Business') && baseType.includes('Business')) ||
          (selType.includes('Frontline') && baseType.includes('Frontline'))
        );
      }
      return true;
    });

    if (compatibleAddons.length === 0 && relevantUpgrades.length === 0) return null;

    return (
      <div className="max-w-xs text-left">
        {compatibleAddons.length > 0 && (
          <div className="mb-1">
            <span className="text-emerald-400">Add-ons: </span>
            <span className="text-white/70">{compatibleAddons.slice(0, 3).map(a => a.name.replace(/Microsoft |Enterprise Mobility \+ Security /g, '')).join(', ')}</span>
          </div>
        )}
        {relevantUpgrades.length > 0 && (
          <div>
            <span className="text-indigo-400">Upgrade to: </span>
            <span className="text-white/70">{relevantUpgrades.slice(0, 3).map(u => u.name.replace(/Microsoft 365 |Office 365 /g, '')).join(', ')}</span>
          </div>
        )}
      </div>
    );
  };

  if (loading && licenses.length === 0) {
    return (
      <div className="app-background flex items-center justify-center">
        <div className="text-center">
          <div className="liquid-glass p-8 inline-block">
            <Loader className="w-12 h-12 text-indigo-400 animate-spin mx-auto mb-4" />
            <p className="theme-copy-muted">Loading license data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-background flex items-center justify-center">
        <div className="liquid-glass p-8 text-center">
          <p className="text-rose-400 mb-4">Error: {error}</p>
          <button onClick={fetchInitialData} className="liquid-glass-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-background">
      {/* Header */}
      <header className="liquid-glass-header">
        <div className="container-xl py-8 header-layout">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-8 h-8 text-indigo-400" />
              <h1 className="heading-xl text-gradient">Microsoft 365</h1>
            </div>
            <p className="header-subtitle ml-11">Compare features across licenses, add-ons, and government tiers</p>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="theme-toggle"
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <SunMedium className="w-4 h-4" /> : <MoonStar className="w-4 h-4" />}
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-xl section-spacing">
        {/* License Selection */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          {/* Side A */}
          <div className="space-y-4">
            <div className="liquid-glass p-6">
              <label className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-4">
                <Shield className="w-4 h-4" />
                Base License
              </label>
              <Select
                options={baseLicenseOptions}
                value={sideABase}
                onChange={setSideABase}
                styles={liquidGlassSelectStyles}
                placeholder="Select base license..."
                isClearable
                classNamePrefix="select"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="liquid-glass p-6" style={{ borderColor: 'rgba(245, 158, 11, 0.15)' }}>
              <label className="flex items-center gap-2 text-sm font-semibold text-amber-300 mb-4">
                <Plus className="w-4 h-4" />
                Add-ons
                {sideABase && sideAAddonOptions.length === 0 && (
                  <span className="text-xs text-rose-400 ml-2">No compatible add-ons</span>
                )}
              </label>
              <Select
                isMulti
                options={sideAAddonOptions}
                value={sideAAddons}
                onChange={setSideAAddons}
                styles={addonSelectStyles}
                placeholder={!sideABase ? "Select a base license first..." : "Add EMS, Defender, Teams..."}
                noOptionsMessage={() => !sideABase ? "Select a base license first" : "No compatible add-ons"}
                classNamePrefix="select"
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          {/* Side B */}
          <div className="space-y-4">
            <div className="liquid-glass p-6">
              <label className="flex items-center gap-2 text-sm font-semibold text-emerald-300 mb-4">
                <Shield className="w-4 h-4" />
                Base License
              </label>
              <Select
                options={baseLicenseOptions}
                value={sideBBase}
                onChange={setSideBBase}
                styles={liquidGlassSelectStyles}
                placeholder="Select base license..."
                isClearable
                classNamePrefix="select"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="liquid-glass p-6" style={{ borderColor: 'rgba(245, 158, 11, 0.15)' }}>
              <label className="flex items-center gap-2 text-sm font-semibold text-amber-300 mb-4">
                <Plus className="w-4 h-4" />
                Add-ons
                {sideBBase && sideBAddonOptions.length === 0 && (
                  <span className="text-xs text-rose-400 ml-2">No compatible add-ons</span>
                )}
              </label>
              <Select
                isMulti
                options={sideBAddonOptions}
                value={sideBAddons}
                onChange={setSideBAddons}
                styles={addonSelectStyles}
                placeholder={!sideBBase ? "Select a base license first..." : "Add EMS, Defender, Teams..."}
                noOptionsMessage={() => !sideBBase ? "Select a base license first" : "No compatible add-ons"}
                classNamePrefix="select"
                menuPortalTarget={document.body}
              />
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          <div className="legend-item">
            <Check className="w-4 h-4 text-emerald-400" />
            <span>Included</span>
          </div>
          <div className="legend-item">
            <X className="w-4 h-4 text-rose-400/60" />
            <span>Not Included</span>
          </div>
          <div className="legend-item feature-addon-ring">
            <Check className="w-4 h-4 text-amber-400" />
            <Plus className="w-3 h-3 text-amber-400 -ml-1" />
            <span>Added by Add-on</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mb-10">
          <button
            onClick={fetchComparison}
            disabled={loading || (sideA.length === 0 && sideB.length === 0)}
            className="liquid-glass-btn flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Comparing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Compare Licenses
              </>
            )}
          </button>
          <button
            onClick={clearAllSelections}
            disabled={!sideABase && sideAAddons.length === 0 && !sideBBase && sideBAddons.length === 0 && !comparisonData}
            className="liquid-glass-btn liquid-glass-btn-secondary flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Clear All
          </button>
        </div>

        {/* Search */}
        {comparisonData && (
          <div className="mb-8">
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 theme-icon-muted" />
              <input
                type="text"
                placeholder="Search features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="liquid-glass-input"
              />
            </div>
          </div>
        )}

        {/* Summary Stats */}
        {comparisonData && comparisonData.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <div className="stat-card">
              <div className="stat-card-value">{comparisonData.summary.total_features}</div>
              <div className="stat-card-label">Total Features</div>
            </div>
            <div className="stat-card stat-card-warning">
              <div className="stat-card-value">{comparisonData.summary.differences}</div>
              <div className="stat-card-label">Differences</div>
            </div>
            <div className="stat-card stat-card-primary">
              <div className="stat-card-value">{comparisonData.summary.side_a_advantages}</div>
              <div className="stat-card-label">Left Advantages</div>
            </div>
            <div className="stat-card stat-card-success">
              <div className="stat-card-value">{comparisonData.summary.side_b_advantages}</div>
              <div className="stat-card-label">Right Advantages</div>
            </div>
          </div>
        )}

        {/* Comparison Table */}
        {comparisonData && (
          <div className="liquid-glass-table overflow-hidden animate-fade-in">
            <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 z-10 table-sticky-head backdrop-blur-xl">
                  <tr>
                    <th className="px-6 py-5 text-left w-2/5">Feature</th>
                    <th className="px-6 py-5 text-center w-[30%] border-l border-white/5">
                      <div className="truncate text-indigo-300" title={sideALabel}>
                        {sideALabel}
                      </div>
                    </th>
                    <th className="px-6 py-5 text-center w-[30%] border-l border-white/5">
                      <div className="truncate text-emerald-300" title={sideBLabel}>
                        {sideBLabel}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map(([category, features], catIndex) => (
                    <React.Fragment key={catIndex}>
                      <tr
                        className="category-row"
                        onClick={() => toggleCategory(category)}
                      >
                        <td colSpan={3} className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 bg-indigo-500 rounded-full" />
                            <span className="font-semibold theme-heading text-base">{category}</span>
                            <span className="text-xs theme-faint">({features.length} features)</span>
                            <ChevronDown
                              className={`w-5 h-5 theme-faint ml-auto chevron-rotate ${expandedCategories.has(category) ? 'expanded' : ''}`}
                            />
                          </div>
                        </td>
                      </tr>
                      {expandedCategories.has(category) && features.map((feature, featIndex) => (
                        <tr
                          key={`${catIndex}-${featIndex}`}
                          className={`border-t border-white/5 hover:bg-white/[0.02] transition-colors ${
                            feature.side_a_value !== feature.side_b_value ? 'feature-row-diff' : ''
                          }`}
                        >
                          <td className="px-6 py-4 text-sm theme-copy-muted">{feature.name}</td>
                          <td className="px-6 py-4 border-l border-white/5">
                            <FeatureCell
                              value={feature.side_a_value}
                              isFromAddon={feature.side_a_from_addon}
                              upgradeHint={feature.side_a_value === false ? getUpgradeHint(feature.id, sideABase) : null}
                            />
                          </td>
                          <td className="px-6 py-4 border-l border-white/5">
                            <FeatureCell
                              value={feature.side_b_value}
                              isFromAddon={feature.side_b_from_addon}
                              upgradeHint={feature.side_b_value === false ? getUpgradeHint(feature.id, sideBBase) : null}
                            />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-16">
        <div className="container-xl py-8 text-center">
          <p className="theme-footer text-sm">
            Data sourced from official Microsoft 365 comparison documents.
            <span className="mx-2">·</span>
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
